import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CheckCircle, ThumbsUp, AlertCircle, AlertTriangle } from 'lucide-react';

interface ActualVsPredictedPoint {
  period: number | string;
  actual: number;
  predicted: number;
  residual?: number;
}

interface ActualVsPredictedProps {
  data: ActualVsPredictedPoint[] | undefined;
  nrmse?: number;
  r2?: number;
  title?: string;
}

// Quality assessment helpers
function getR2Quality(r2: number) {
  if (r2 > 0.85) return { level: 'excellent', label: 'Excellent Fit', color: 'text-emerald-700', bgColor: 'bg-emerald-100', borderColor: 'border-emerald-400' };
  if (r2 > 0.70) return { level: 'good', label: 'Good Fit', color: 'text-blue-700', bgColor: 'bg-blue-100', borderColor: 'border-blue-400' };
  if (r2 > 0.50) return { level: 'fair', label: 'Fair Fit', color: 'text-amber-700', bgColor: 'bg-amber-100', borderColor: 'border-amber-400' };
  return { level: 'poor', label: 'Weak Fit', color: 'text-red-700', bgColor: 'bg-red-100', borderColor: 'border-red-400' };
}

function getNRMSEQuality(nrmse: number) {
  const pct = nrmse * 100;
  if (pct < 10) return { level: 'excellent', label: 'Highly Accurate', color: 'text-emerald-700', bgColor: 'bg-emerald-100' };
  if (pct < 15) return { level: 'good', label: 'Accurate', color: 'text-blue-700', bgColor: 'bg-blue-100' };
  if (pct < 25) return { level: 'fair', label: 'Moderate', color: 'text-amber-700', bgColor: 'bg-amber-100' };
  return { level: 'poor', label: 'Low Accuracy', color: 'text-red-700', bgColor: 'bg-red-100' };
}

export const ActualVsPredicted: React.FC<ActualVsPredictedProps> = ({
  data,
  nrmse,
  r2,
  title = 'Actual vs Predicted Response',
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-96 bg-gray-50 rounded">
          <p className="text-gray-500">No actual vs predicted data available from model results</p>
        </div>
      </div>
    );
  }

  const chartData = data;
  const r2Quality = r2 !== undefined ? getR2Quality(r2) : null;
  const nrmseQuality = nrmse !== undefined ? getNRMSEQuality(nrmse) : null;

  // Determine which icon to use based on R² quality
  const QualityIcon = r2Quality?.level === 'excellent' ? CheckCircle :
                      r2Quality?.level === 'good' ? ThumbsUp :
                      r2Quality?.level === 'fair' ? AlertCircle : AlertTriangle;

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <div className="flex gap-4">
          {/* R² Badge with Quality Indicator */}
          {r2 !== undefined && r2Quality && (
            <div className={`px-4 py-2 rounded-xl ${r2Quality.bgColor} border ${r2Quality.borderColor}`}>
              <div className="flex items-center gap-2">
                <QualityIcon className={`w-5 h-5 ${r2Quality.color}`} />
                <div>
                  <p className="text-xs text-gray-600">Model explains</p>
                  <p className={`text-lg font-bold ${r2Quality.color}`}>{(r2 * 100).toFixed(0)}% of changes</p>
                </div>
              </div>
            </div>
          )}
          {/* NRMSE Badge */}
          {nrmse !== undefined && nrmseQuality && (
            <div className={`px-4 py-2 rounded-xl ${nrmseQuality.bgColor} border border-gray-200`}>
              <p className="text-xs text-gray-600">Prediction error</p>
              <p className={`text-lg font-bold ${nrmseQuality.color}`}>{(nrmse * 100).toFixed(1)}%</p>
            </div>
          )}
        </div>
      </div>

      {/* Quality Summary */}
      {r2Quality && (
        <div className={`mb-4 px-4 py-3 rounded-lg ${r2Quality.bgColor} border ${r2Quality.borderColor}`}>
          <div className="flex items-center gap-2">
            <QualityIcon className={`w-5 h-5 ${r2Quality.color}`} />
            <span className={`font-semibold ${r2Quality.color}`}>{r2Quality.label}</span>
            <span className="text-gray-600 text-sm">
              {r2Quality.level === 'excellent' && '— The predicted line closely follows actual values. You can trust these predictions.'}
              {r2Quality.level === 'good' && '— Predictions track actual values well. Results are reliable.'}
              {r2Quality.level === 'fair' && '— Some divergence between predicted and actual. Consider adding more data or channels.'}
              {r2Quality.level === 'poor' && '— Significant gaps between predicted and actual. Model may need more refinement.'}
            </span>
          </div>
        </div>
      )}

      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="period"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 11 }}
          />
          <YAxis label={{ value: 'Response Value', angle: -90, position: 'insideLeft' }} />
          <Tooltip
            contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #ccc' }}
            formatter={(value: any) => value.toFixed(2)}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#8884d8"
            name="Actual Revenue"
            dot={false}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="predicted"
            stroke="#82ca9d"
            name="Model Prediction"
            dot={false}
            strokeWidth={2}
            strokeDasharray="5 5"
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-gray-600">
        <p>
          <strong>How to read this chart:</strong> The purple line is your actual revenue over time.
          The green dashed line is what the model predicted. When these lines are close together,
          the model is accurately capturing the patterns in your data.
        </p>
      </div>
    </div>
  );
};
